import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { TFetchStatus } from "./types";

// Get all invoice percentages
export const fetchInvoicePercentages = createAsyncThunk(
    "invoicePercentages/fetchInvoicePercentages",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/company/commissions`);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Get one invoice percentage
export const fetchInvoicePercentage = createAsyncThunk(
    "invoicePercentages/fetchInvoicePercentage",
    async ({ percentageId }: { percentageId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/company/commissions/${percentageId}`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Create an invoice percentage
export const createInvoicePercentage = createAsyncThunk(
    "invoicePercentages/createInvoicePercentage",
    async ({ data }: { data: Omit<InvoicePercentage, "id"> }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/company/commissions`, data);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Update an invoice percentage
export const updateInvoicePercentage = createAsyncThunk(
    "invoicePercentages/updateInvoicePercentage",
    async (
        { percentageId, data }: { percentageId: number; data: Omit<InvoicePercentage, "id"> },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/company/commissions/${percentageId}`,
                data,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Delete an invoice percentage
export const deleteInvoicePercentage = createAsyncThunk(
    "invoicePercentages/deleteInvoicePercentage",
    async ({ percentageId }: { percentageId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/company/commissions/${percentageId}`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface PercentagesFilters {
    channelIds: number[] | "all";
    countryCodes: string[] | "all";
}

export interface InvoicePercentage {
    id: number;
    name: string;
    percentage: number;
    filters: PercentagesFilters;
}

interface InitialState {
    invoicePercentage: InvoicePercentage;
    invoicePercentages: InvoicePercentage[];
    invoicePercentagesStatus: TFetchStatus;
}

const initialState: InitialState = {
    invoicePercentage: {} as InvoicePercentage,
    invoicePercentages: [],
    invoicePercentagesStatus: "idle",
};

export const invoicePercentagesSlice = createSlice({
    name: "invoicePercentages",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchInvoicePercentages.fulfilled, (state, { payload }) => {
            state.invoicePercentages = payload;
            state.invoicePercentagesStatus = "succeeded";
        });
        builder.addCase(fetchInvoicePercentages.rejected, (state, { payload }) => {
            state.invoicePercentagesStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchInvoicePercentage.fulfilled, (state, { payload }) => {
            state.invoicePercentage = payload;
        });
        builder.addCase(fetchInvoicePercentage.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(createInvoicePercentage.fulfilled, (state, { payload }) => {
            state.invoicePercentages = [...state.invoicePercentages, payload];
            toast.success(`${t("InvoicePercentages.CreationSuccess")}`);
        });
        builder.addCase(createInvoicePercentage.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            if ((payload as any)?.message === "ServerError.ResourceAlreadyExists") {
                toast.error(`${t("InvoicePercentages.Form.AlreadyExists")}`, {
                    toastId: "ServerError.InternalServerError",
                });
            } else {
                toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            }
        });
        builder.addCase(updateInvoicePercentage.fulfilled, (state, { payload }) => {
            state.invoicePercentages = state.invoicePercentages.map((percentage) => {
                if (percentage.id === payload.id) {
                    return payload;
                } else {
                    return percentage;
                }
            });
            toast.success(`${t("InvoicePercentages.EditSuccess")}`);
        });
        builder.addCase(updateInvoicePercentage.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(deleteInvoicePercentage.fulfilled, (state, action) => {
            state.invoicePercentages = state.invoicePercentages.filter(
                (percentage) => percentage.id !== action.meta.arg.percentageId,
            );
            toast.success(`${t("InvoicePercentages.DeleteSuccess")}`);
        });
        builder.addCase(deleteInvoicePercentage.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
    },
});

export default invoicePercentagesSlice.reducer;
